import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { accessibilityPreferenceStorage } from '@/lib/accessibility/storage';
import { DEFAULT_ACCESSIBILITY_PREFERENCES } from '@/lib/accessibility/types';
import type { AccessibilityPreferences } from '@/lib/accessibility/types';

interface UseAccessibilityPreferencesResult {
  preferences: AccessibilityPreferences;
  setPreference: <K extends keyof AccessibilityPreferences>(key: K, value: AccessibilityPreferences[K]) => void;
  reset: () => void;
}

// Site-scoped, localStorage-backed visitor preferences — never touches the
// unit's saved theme_config. `siteId` is ideally the stable `sites.id`
// UUID (see use-public-site.ts); callers that don't have it yet (e.g. the
// public site route while its data is still loading) may pass a temporary
// identifier — the effect below reloads from storage whenever `siteId`
// changes, so swapping in the real id once data resolves picks up whatever
// was actually saved for this site rather than being stuck with whatever
// loaded under the temporary key.
export function useAccessibilityPreferences(siteId: string): UseAccessibilityPreferencesResult {
  const [preferences, setPreferences] = useState<AccessibilityPreferences>(() =>
    accessibilityPreferenceStorage.load(siteId)
  );
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPreferences(accessibilityPreferenceStorage.load(siteId));
  }, [siteId]);

  const setPreference = useCallback(
    <K extends keyof AccessibilityPreferences>(key: K, value: AccessibilityPreferences[K]) => {
      setPreferences((previous) => {
        const next = { ...previous, [key]: value };
        accessibilityPreferenceStorage.save(siteId, next);
        return next;
      });
    },
    [siteId]
  );

  const reset = useCallback(() => {
    accessibilityPreferenceStorage.clear(siteId);
    setPreferences(DEFAULT_ACCESSIBILITY_PREFERENCES);
  }, [siteId]);

  return useMemo(() => ({ preferences, setPreference, reset }), [preferences, setPreference, reset]);
}
